import { ApolloClient, ApolloLink, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { clientHeaders } from "@constants/headers";
import { getOrCreateDeviceId, getToken, removeToken, TokenType } from "@cpj-ai/panel-npm-package";
import ApolloLinkTimeout from "apollo-link-timeout";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import toast from "react-hot-toast";

const timeoutLink = new ApolloLinkTimeout(100000);

const apolloClientProps = {
  uri: import.meta.env.VITE_PUBLIC_API_URL,
  cache: new InMemoryCache(),
  connectToDevTools: true,
};

const _apolloClient = new ApolloClient(apolloClientProps);

const authLink = setContext(async (_, { headers }) => {
  const authToken = await getToken(_apolloClient, TokenType.ORG_AUTH);
  const adminToken = await getToken(_apolloClient, TokenType.ADMIN_AUTH);
  const hijackedCompanyToken = await getToken(_apolloClient, TokenType.HIJACKED_COMPANY);

  const newHeaders = { ...headers };

  if (authToken) {
    newHeaders.Authorization = `${clientHeaders.jwtPrefix} ${authToken.token}`;
  }

  if (hijackedCompanyToken) {
    newHeaders.Authorization = `${clientHeaders.jwtPrefix} ${adminToken.token}`;
    newHeaders["Authorization-Hijack"] = `${clientHeaders.jwtPrefix} ${hijackedCompanyToken.token}`;
  }

  newHeaders[clientHeaders.deviceId] = getOrCreateDeviceId();

  return { headers: newHeaders };
});

const errorLink = onError(({ networkError, graphQLErrors }) => {
  if (networkError && "statusCode" in networkError) {
    switch (networkError.statusCode) {
      case 403:
        removeToken(TokenType.ORG_AUTH);
        removeToken(TokenType.HIJACKED_COMPANY);
        window.location.reload();
        break;
      default:
        break;
    }
  }

  graphQLErrors?.forEach(({ extensions }) => {
    switch (extensions?.status_code) {
      case 429:
        toast.error("Too many requests. Please try again later.");
        break;
      default:
        break;
    }
    return extensions;
  });
});

const uploadLink = createUploadLink({
  uri: import.meta.env.VITE_PUBLIC_API_URL,
  credentials: "same-origin",
  fetchOptions: {
    timeout: 10000,
  },
});

const createApolloClient = (cache = {}) => {
  return new ApolloClient({
    ...apolloClientProps,
    cache: new InMemoryCache().restore(cache),
    link: ApolloLink.from([authLink, errorLink, timeoutLink, uploadLink]),
  });
};

const apolloClient = createApolloClient();

export default apolloClient;
